import * as React from "react"

import { cn } from "@/lib/utils"

// Phase 3, Task 13.3: Card hover effects

const Card = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      "rounded-2xl border border-calm-200 bg-white text-calm-900 shadow-card transition-shadow hover:shadow-card-hover",
      className
    )}
    {...props}
  />
))
Card.displayName = "Card"

/**
 * Interactive Card with hover lift effect
 * Task 13.3: scale(1.02) + shadow-lg with ease-out 150ms
 */
const InteractiveCard = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      "rounded-2xl border border-calm-200 bg-white text-calm-900",
      "shadow-card",
      // Task 13.3: Hover lift effect
      "transition-all duration-150 ease-out",
      "hover:scale-[1.02] hover:shadow-lg",
      "active:scale-[0.99] active:shadow-card",
      // Cursor for clickable cards
      "cursor-pointer",
      className
    )}
    {...props}
  />
))
InteractiveCard.displayName = "InteractiveCard"

const CardHeader = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn("flex flex-col space-y-1.5 p-6", className)}
    {...props}
  />
))
CardHeader.displayName = "CardHeader"

const CardTitle = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLHeadingElement>
>(({ className, ...props }, ref) => (
  <h3
    ref={ref}
    className={cn(
      "text-2xl font-semibold leading-none tracking-tight",
      className
    )}
    {...props}
  />
))
CardTitle.displayName = "CardTitle"

const CardDescription = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(({ className, ...props }, ref) => (
  <p
    ref={ref}
    className={cn("text-sm text-calm-500", className)}
    {...props}
  />
))
CardDescription.displayName = "CardDescription"

const CardContent = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div ref={ref} className={cn("p-6 pt-0", className)} {...props} />
))
CardContent.displayName = "CardContent"

const CardFooter = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn("flex items-center p-6 pt-0", className)}
    {...props}
  />
))
CardFooter.displayName = "CardFooter"

export { Card, InteractiveCard, CardHeader, CardFooter, CardTitle, CardDescription, CardContent }
